import { DEFAULT_PRODUCTION_COMPANY_ID } from "./../../../utils/constants";
import { groupClientsByType } from "./groupClientsByType";
import { useEffect, useMemo, useState } from "react";
import { useGetAllAdvertisersFullQuery } from "../../../api/advertiserAPI";
import { useGetAllBrandsFullQuery } from "../../../api/brandsAPI";
import { useGetAllClientsQuery } from "../../../api/clientAPI";
import { useGetAllIndustriesQuery } from "../../../api/industryAPI";
import {
  AdvertiserT,
  BrandT,
  ClientT,
  SelectOptionT,
} from "../../../models/models";
import { useAppSelector } from "../../../store/hooks";
import { selectCurrentLang } from "../../../store/selectors/appSelectors";
import { ClientTypeE, LangE } from "../../../models/enums";
import filterPostProdList from "../../../utils/helpers/filterPostProdList";
import { selectCurrentUser } from "../../../store/selectors/authSelectors";

// `skip` is OPT-IN and defaults to false: every existing caller (the wizards, FindCampaign,
// useLastBrandAndIndustryIds, ClientReport) keeps eager-loading unchanged. Only the search
// autocomplete passes skip:true, because it does not need any of these lists until the user actually
// engages with the search box — and eagerly loading them cost ~1.68 MB on every cold page load
// (spec 34 D1).
type UseSelectOptionsT = (config?: {
  withOnlyParentClients?: boolean;
  skip?: boolean;
}) => {
  advertisersSO: SelectOptionT[];
  brandsSO: SelectOptionT[];
  broadcastersSO: SelectOptionT[];
  industrySO: SelectOptionT[];
  postProductionCompaniesSO: SelectOptionT[];
  filteredPostProductionCompaniesSO: SelectOptionT[];
  productionCompaniesSO: SelectOptionT[];
  productionCompaniesWithNoValueSO: SelectOptionT[];
  advertisingAgenciesSO: SelectOptionT[];
  mediaCompaniesSO: SelectOptionT[];
  mediaCompaniesAllSO: SelectOptionT[];
  clientsSO: SelectOptionT[];
  isFetchingSelectOptions: boolean;
};

export const useSelectOptions: UseSelectOptionsT = (config) => {
  const user = useAppSelector(selectCurrentUser);
  const lang = useAppSelector(selectCurrentLang);
  // RTK Query takes `skip` as its SECOND hook argument — putting it in the first would silently make
  // it part of the cache key instead of skipping anything.
  const skip = config?.skip ?? false;
  const { data: advertisers = [], isFetching: isFetchingAdvertisers } =
    useGetAllAdvertisersFullQuery({ asSelectOptions: false }, { skip });
  const advertisersSO = useMemo(
    () =>
      (advertisers as any)?.map((adv: AdvertiserT) => ({
        value: adv.Id,
        label: adv.Name,
        shortName: (adv as any).ShortName,
      })),
    [advertisers]
  );
  const { data: brands = [], isFetching: isFetchingBrands } =
    useGetAllBrandsFullQuery({ asSelectOptions: false }, { skip });
  const brandsSO = useMemo(
    () =>
      (brands as any)?.map((brand: BrandT) => ({
        value: +brand.Id,
        label: brand.Name,
        shortName: (brand as any).ShortName,
      })),
    [brands]
  );
  const { data: industrySO = [], isFetching: isFetchingIndustry } =
    useGetAllIndustriesQuery({ asSelectOptions: false }, { skip });
  const { data: clients, isFetching: isFetchingClients } =
    useGetAllClientsQuery(
      {
        withOnlyParentClients: config?.withOnlyParentClients,
      },
      { skip }
    );
  const broadcastersSO = useMemo(
    () =>
      clients
        ? clients
            .filter(
              (client: ClientT) =>
                +client.ClientType === ClientTypeE.Broadcaster
            )
            .map((client: ClientT) => ({
              value: +client.Id,
              label: client.Name,
            }))
        : [],
    [clients]
  );
  const [postProductionCompaniesSO, setPostProductionCompaniesSO] = useState(
    []
  );
  const [
    filteredPostProductionCompaniesSO,
    setFilteredPostProductionCompaniesSO,
  ] = useState([]);
  const [productionCompaniesSO, setProductionCompaniesSO] = useState([]);
  const [advertisingAgenciesSO, setAdvertisingAgenciesSO] = useState([]);

  const isFetching = useMemo(
    () =>
      isFetchingAdvertisers ||
      isFetchingBrands ||
      isFetchingIndustry ||
      isFetchingClients,
    [
      isFetchingAdvertisers,
      isFetchingBrands,
      isFetchingIndustry,
      isFetchingClients,
    ]
  );

  useEffect(() => {
    if (clients) {
      const [postProduction, production, advAgencies] =
        groupClientsByType(clients);
      setPostProductionCompaniesSO(postProduction);
      setFilteredPostProductionCompaniesSO(
        postProduction.filter((item) => filterPostProdList(item, user))
      );
      setProductionCompaniesSO(production);
      setAdvertisingAgenciesSO(advAgencies);
    }
  }, [clients]);

  const mappedIndustrySO = useMemo(
    () =>
      industrySO.map(({ DisplayNameEn, DisplayNameHe, Id }) => ({
        value: Id,
        label: lang === LangE.EN ? DisplayNameEn : DisplayNameHe,
      })),
    [industrySO, lang]
  );

  // Country of the logged-in advertising agency (the current user's own client record). Used to
  // scope the media-company dropdown to same-country media companies.
  const agencyCountry = useMemo(
    () => (clients as ClientT[])?.find((c) => +c.Id === +user?.id)?.Country,
    [clients, user?.id]
  );

  // Media companies (ClientType = MediaCompany) in the agency's country. The mandatory
  // "No media company" option is prepended by the campaign wizard (translated there).
  const mediaCompaniesSO = useMemo(
    () =>
      clients
        ? (clients as ClientT[])
            .filter(
              (client) =>
                +client.ClientType === ClientTypeE["Media Company"] &&
                !!agencyCountry &&
                client.Country === agencyCountry
            )
            .map((client) => ({ value: +client.Id, label: client.Name }))
        : [],
    [clients, agencyCountry]
  );

  // All media companies (ClientType = MediaCompany) regardless of country. Used by the search
  // autocomplete on the campaigns/deliveries pages — unlike the wizard, search must not scope to the
  // searcher's country (a GlobalAdmin has no country, and the other entity lists are unfiltered too).
  const mediaCompaniesAllSO = useMemo(
    () =>
      clients
        ? (clients as ClientT[])
            .filter(
              (client) =>
                +client.ClientType === ClientTypeE["Media Company"]
            )
            .map((client) => ({ value: +client.Id, label: client.Name }))
        : [],
    [clients]
  );

  return {
    advertisersSO,
    brandsSO,
    industrySO: mappedIndustrySO,
    postProductionCompaniesSO,
    filteredPostProductionCompaniesSO,
    productionCompaniesSO,
    productionCompaniesWithNoValueSO: [
      { value: DEFAULT_PRODUCTION_COMPANY_ID, label: "No production company" },
      ...productionCompaniesSO,
    ],
    advertisingAgenciesSO,
    mediaCompaniesSO,
    mediaCompaniesAllSO,
    clientsSO:
      clients?.map((client) => ({ value: +client.Id, label: client.Name })) ||
      [],
    broadcastersSO,
    isFetchingSelectOptions: isFetching,
  };
};
